import { useState } from "react";
import { createNewModel } from "../../utils/fine-tuning-functions";

export default function CreateMIModel() {
  const [modelData, setModelData] = useState({
    modelName: "",
    modelDescription: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setModelData((prevModelData) => ({
      ...prevModelData,
      [id]: value
    }));
    
    // Clear messages when user starts typing again
    if (success || error) {
      setSuccess(false);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(false);
      
      await createNewModel(modelData);
      
      setSuccess(true);
      setModelData({
        modelName: "",
        modelDescription: ""
      });
    } catch (error) {
      console.error("Error creating model:", error);
      setError("Failed to create model. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <header className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-ktx-purple">
          Create New Model
        </h1>
        <p className="mt-2 text-gray-600">
          Create a custom model that can be fine-tuned with your specific datasets
        </p>
      </header>

      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Model Name Input */}
          <div>
            <label htmlFor="modelName" className="block text-sm font-medium text-gray-700 mb-1">
              Model Name
            </label>
            <input
              type="text"
              id="modelName"
              value={modelData.modelName}
              onChange={handleInputChange}
              required
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
              placeholder="Enter a distinctive name for your model"
            />
            <p className="mt-1 text-xs text-gray-500">
              Choose a clear, descriptive name that indicates the model's purpose
            </p>
          </div>

          {/* Model Description Input */}
          <div>
            <label htmlFor="modelDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Model Description
            </label>
            <textarea
              id="modelDescription"
              value={modelData.modelDescription}
              onChange={handleInputChange}
              required
              rows="4"
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
              placeholder="Describe the purpose, use case, and expected functionality of this model"
            />
            <p className="mt-1 text-xs text-gray-500">
              A detailed description will help you and others understand the model's purpose later
            </p>
          </div>

          {/* Success message */}
          {success && (
            <div className="rounded-md bg-green-50 p-4 border border-green-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">
                    Model created successfully! You can now configure it for fine-tuning.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="rounded-md bg-red-50 p-4 border border-red-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple ${
                isSubmitting ? 'bg-ktx-purple-light cursor-not-allowed' : 'bg-ktx-purple hover:bg-ktx-purple-dark'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Model'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Information card */}
      <div className="mt-8 bg-white p-5 rounded-lg shadow-sm border border-gray-200">
        <h4 className="font-medium text-ktx-purple flex items-center mb-4">
          <svg className="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Model Creation Guidelines
        </h4>
        <div className="text-sm text-gray-600 space-y-3">
          <p>Creating a new model is the first step in the fine-tuning process. After creation:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Configure your model by selecting a base model and dataset</li>
            <li>Start the fine-tuning process to train on your custom data</li>
            <li>Deploy your fine-tuned model to make it available via API</li>
          </ol>
          <p className="mt-3 text-blue-600 font-medium">
            Well-described models with clear naming conventions are easier to manage in a team environment.
          </p>
        </div>
      </div>
    </div>
  );
}